import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, useApi } from '../context/AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatBirthdate = (dateString) => {
  if (!dateString) return 'Not set';
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return 'Not set';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const combineAddress = ({ street, city, state, zip }) =>
  [street, city, state, zip].filter(Boolean).join(', ');

const centsToDisplay = (cents) =>
  cents != null ? `$${(cents / 100).toFixed(2)}` : '—';

const TIER_COLORS = {
  bronze:   'text-amber-700 bg-amber-50 border-amber-200',
  silver:   'text-gray-600 bg-gray-50 border-gray-200',
  gold:     'text-yellow-600 bg-yellow-50 border-yellow-200',
  platinum: 'text-purple-700 bg-purple-50 border-purple-200',
};

const VERIFY_STATUS_INFO = {
  unverified:     { label: 'Not Verified',  color: 'bg-gray-100 text-gray-600',   banner: 'bg-blue-50 border-blue-200 text-blue-800' },
  pending_upload: { label: 'Uploading…',    color: 'bg-yellow-100 text-yellow-700', banner: 'bg-yellow-50 border-yellow-200 text-yellow-800' },
  pending_admin:  { label: 'Under Review',  color: 'bg-blue-100 text-blue-700',   banner: 'bg-blue-50 border-blue-200 text-blue-800' },
  verified:       { label: 'Verified ✓',    color: 'bg-green-100 text-green-700', banner: '' },
  rejected:       { label: 'Rejected',      color: 'bg-red-100 text-red-700',     banner: 'bg-red-50 border-red-200 text-red-800' },
};

// Prominent (not buried in a dropdown) verification nudge shown at the top of
// the Account tab so every unverified user sees it every time they visit their profile.
function VerificationStatusCard({ api }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get('/users/me/verify/status')
      .then(r => setStatus(r.data?.verificationStatus || 'unverified'))
      .catch(() => setStatus('unverified'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status || status === 'verified') return null;

  const info = VERIFY_STATUS_INFO[status] || VERIFY_STATUS_INFO.unverified;
  const isActionable = status === 'unverified' || status === 'rejected';

  return (
    <div className={`mb-6 border rounded-xl p-4 flex items-center justify-between gap-4 ${info.banner}`}>
      <div>
        <p className="font-semibold text-sm">
          {status === 'pending_admin' ? 'Documents under review' : status === 'rejected' ? 'Verification rejected' : 'Identity verification required'}
        </p>
        <p className="text-sm opacity-90 mt-0.5">
          {status === 'pending_admin'
            ? "We're reviewing your driver's license and insurance card — usually within 24 hours."
            : status === 'rejected'
            ? 'One or more of your documents could not be verified. Please resubmit.'
            : "You'll need to upload your driver's license and proof of insurance before you can book a vehicle."}
        </p>
      </div>
      {isActionable && (
        <Link to="/verify" className="shrink-0 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 transition">
          Verify Now
        </Link>
      )}
    </div>
  );
}

const BOOKING_STATUS_COLORS = {
  pending:   'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  active:    'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-600',
  canceled:  'bg-red-100 text-red-600',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
        active
          ? 'border-blue-600 text-blue-600 bg-white'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
      <span className="font-semibold text-gray-700 col-span-1">{label}</span>
      <span className="text-gray-800 col-span-2">{value || 'Not set'}</span>
    </div>
  );
}

// ── Account Tab ───────────────────────────────────────────────────────────────

function AccountTab({ api, user, displayName, email, onLogout }) {
  const [formData, setFormData] = useState({ name: '', birthdate: '', street: '', city: '', state: '', zip: '', address: '', picture: '', dob: '', dlNumber: '', dlState: '' });
  const [savedData, setSavedData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user?.profile?.sub || isFetching) return;
    setIsFetching(true);
    api.get('/users/me')
      .then((res) => {
        const d = res.data;
        const parts = (d.address || '').split(', ');
        const profile = {
          name:     d.fullName || d.name || user.profile.name || '',
          birthdate: d.birthdate || '',
          address:  d.address || '',
          street:   parts[0] || '',
          city:     parts[1] || '',
          state:    parts[2] || '',
          zip:      parts[3] || '',
          picture:  d.picture || user.profile.picture || '',
          dob:       d.dob || '',
          dlNumber:  d.dlNumber || '',
          dlState:   d.dlState || '',
        };
        setFormData(profile);
        setSavedData(profile);
        localStorage.setItem('user_display_name', profile.name);
      })
      .catch((err) => console.error('Profile fetch error', err))
      .finally(() => setIsFetching(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile?.sub]);

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage(''); setError(null);
    const combined = combineAddress({ street: formData.street, city: formData.city, state: formData.state, zip: formData.zip });
    const payload = { ...formData, address: combined, fullName: formData.name };
    try {
      await api.put('/users/me', payload);
      localStorage.setItem('user_display_name', formData.name);
      window.dispatchEvent(new Event('profileUpdated'));
      setSavedData({ ...formData, address: combined });
      setMessage('Profile updated successfully!');
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile.');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('File too large. Max 10 MB.'); return; }
    setError(null); setIsUploading(true);
    try {
      const { data } = await api.post('/users/me/profile-picture', { fileName: file.name, fileType: file.type });
      await fetch(data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      await api.put('/users/me', { ...formData, picture: data.fileUrl });
      setFormData((p) => ({ ...p, picture: data.fileUrl }));
      setMessage('Profile picture updated!');
    } catch (err) {
      setError('Failed to upload picture.');
    } finally {
      setIsUploading(false);
    }
  };

  if (isFetching) return <div className="py-8 text-center text-gray-500">Loading profile…</div>;

  return (
    <div>
      {/* Avatar + welcome */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex flex-col items-center">
          <div
            className="relative w-20 h-20 rounded-full cursor-pointer group border-4 border-blue-100 overflow-hidden"
            onClick={() => fileInputRef.current?.click()}
            title="Click to upload new picture"
          >
            {formData.picture ? (
              <img src={formData.picture} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-3xl font-bold">
                {(formData.name || displayName || '?')[0].toUpperCase()}
              </div>
            )}
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <svg className="animate-spin h-8 w-8 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
            )}
            <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity" />
          </div>
          {formData.picture && (
            <a href={formData.picture} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1">
              View full size
            </a>
          )}
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Welcome, {displayName}!</h2>
          <p className="text-gray-500 text-sm">Manage your account details below.</p>
        </div>
        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
      </div>

      <VerificationStatusCard api={api} />

      {message && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">{message}</div>}
      {error   && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      {isEditing ? (
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
              className="w-full px-4 py-2 border rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Birthdate</label>
            <input type="date" value={formData.birthdate} onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
            <input type="text" placeholder="Street" value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg border-gray-300 mb-2 focus:border-blue-500" />
            <input type="text" placeholder="City" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg border-gray-300 mb-2 focus:border-blue-500" />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="State" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="px-4 py-2 border rounded-lg border-gray-300 focus:border-blue-500" />
              <input type="text" placeholder="ZIP" value={formData.zip} onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="px-4 py-2 border rounded-lg border-gray-300 focus:border-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Driver's License #</label>
            <input type="text" placeholder="D123-4567-8901" value={formData.dlNumber} onChange={(e) => setFormData({ ...formData, dlNumber: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">License State</label>
            <input type="text" placeholder="WI" maxLength={2} value={formData.dlState} onChange={(e) => setFormData({ ...formData, dlState: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Email (read-only)</label>
            <input type="email" value={email} disabled className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="flex-1 py-2.5 rounded-lg text-white bg-blue-600 hover:bg-blue-700 font-medium">Save Changes</button>
            <button type="button" onClick={() => { setIsEditing(false); setFormData(savedData); setError(null); }}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
          </div>
        </form>
      ) : (
        <div>
          <div className="space-y-3 mb-6">
            <InfoRow label="Name"      value={formData.name} />
            <InfoRow label="Email"     value={email} />
            <InfoRow label="Birthdate" value={formatBirthdate(formData.birthdate)} />
            <InfoRow label="Address"   value={formData.address} />
            <InfoRow label="Driver's License #" value={formData.dlNumber} />
            <InfoRow label="License State" value={formData.dlState} />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setIsEditing(true)} className="flex-1 py-2.5 rounded-lg text-white bg-blue-600 hover:bg-blue-700 font-medium">Edit Profile</button>
            <button onClick={onLogout} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium">Logout</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bookings Tab ──────────────────────────────────────────────────────────────

function BookingsTab({ api }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    api.get('/users/me/bookings')
      .then((res) => {
        const data = res.data;
        setBookings(Array.isArray(data) ? data : data.bookings || []);
      })
      .catch(() => setError('Could not load bookings.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="py-8 text-center text-gray-500">Loading bookings…</div>;
  if (error)   return <div className="py-8 text-center text-red-500">{error}</div>;
  if (bookings.length === 0) return (
    <div className="py-12 text-center">
      <p className="text-gray-500 mb-4">No bookings yet.</p>
      <Link to="/#vehicles" className="text-blue-600 hover:underline text-sm">Browse our fleet →</Link>
    </div>
  );

  return (
    <div className="space-y-4">
      {bookings.map((b) => {
        const statusCls = BOOKING_STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600';
        return (
          <Link
            key={b.bookingId || b.PK}
            to={`/booking/${b.bookingId || b.PK?.replace('BOOKING#', '')}`}
            className="block p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-sm transition"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-800">{b.vehicleName || b.vin || 'Vehicle'}</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {formatDate(b.startTime)} → {formatDate(b.endTime)}
                </p>
                {b.source === 'turo' && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full mt-1 inline-block">Turo</span>
                )}
              </div>
              <div className="text-right shrink-0">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusCls}`}>
                  {b.status?.charAt(0).toUpperCase() + b.status?.slice(1)}
                </span>
                <p className="text-sm font-bold text-gray-800 mt-1">{centsToDisplay(b.totalAmountCents)}</p>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// ── Rewards Tab ───────────────────────────────────────────────────────────────

function RewardsTab({ api }) {
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/users/me/loyalty')
      .then((res) => setLoyalty(res.data))
      .catch(() => setError('Could not load rewards.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="py-8 text-center text-gray-500">Loading rewards…</div>;
  if (error)   return <div className="py-8 text-center text-red-500">{error}</div>;
  if (!loyalty) return <div className="py-8 text-center text-gray-500">No rewards data found.</div>;

  const tier      = loyalty.tier || 'bronze';
  const tierCls   = TIER_COLORS[tier] || TIER_COLORS.bronze;
  const points    = loyalty.points || 0;
  const rentals   = loyalty.totalRentals || 0;
  const spend     = loyalty.totalSpendCents || 0;
  const credits   = loyalty.freeRentalCredits || 0;

  const TIER_THRESHOLDS = [
    { name: 'Bronze',   minRentals: 0,  minSpend: 0,      discount: 0 },
    { name: 'Silver',   minRentals: 5,  minSpend: 50000,  discount: 5 },
    { name: 'Gold',     minRentals: 15, minSpend: 150000, discount: 10 },
    { name: 'Platinum', minRentals: 30, minSpend: 300000, discount: 15 },
  ];

  const nextTier = TIER_THRESHOLDS.find((t) => t.name.toLowerCase() !== tier &&
    (rentals < t.minRentals || spend < t.minSpend) &&
    TIER_THRESHOLDS.indexOf(t) > TIER_THRESHOLDS.findIndex((t2) => t2.name.toLowerCase() === tier)
  );

  return (
    <div className="space-y-6">
      {/* Tier badge */}
      <div className={`flex items-center gap-4 p-5 rounded-xl border ${tierCls}`}>
        <div className="text-4xl">
          {tier === 'platinum' ? '💎' : tier === 'gold' ? '🥇' : tier === 'silver' ? '🥈' : '🥉'}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Current Tier</p>
          <p className="text-2xl font-bold capitalize">{tier}</p>
          {tier !== 'bronze' && (
            <p className="text-sm opacity-80 mt-0.5">
              {tier === 'silver' ? '5%' : tier === 'gold' ? '10%' : '15%'} discount on all rentals
            </p>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-blue-50 rounded-xl text-center">
          <p className="text-3xl font-bold text-blue-700">{points.toLocaleString()}</p>
          <p className="text-sm text-blue-600 mt-1">Points Balance</p>
          <p className="text-xs text-blue-400 mt-0.5">100 pts = $1 off</p>
        </div>
        <div className="p-4 bg-green-50 rounded-xl text-center">
          <p className="text-3xl font-bold text-green-700">{rentals}</p>
          <p className="text-sm text-green-600 mt-1">Total Rentals</p>
        </div>
        <div className="p-4 bg-purple-50 rounded-xl text-center">
          <p className="text-3xl font-bold text-purple-700">{centsToDisplay(spend)}</p>
          <p className="text-sm text-purple-600 mt-1">Total Spent</p>
        </div>
        <div className="p-4 bg-yellow-50 rounded-xl text-center">
          <p className="text-3xl font-bold text-yellow-700">{credits}</p>
          <p className="text-sm text-yellow-600 mt-1">Free Day Credits</p>
        </div>
      </div>

      {/* Progress to next tier */}
      {nextTier && (
        <div className="p-4 bg-gray-50 rounded-xl">
          <p className="text-sm font-semibold text-gray-700 mb-2">Progress to {nextTier.name}</p>
          <div className="space-y-2">
            {rentals < nextTier.minRentals && (
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Rentals</span>
                  <span>{rentals} / {nextTier.minRentals}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(100, (rentals / nextTier.minRentals) * 100)}%` }} />
                </div>
              </div>
            )}
            {spend < nextTier.minSpend && (
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Spend</span>
                  <span>{centsToDisplay(spend)} / {centsToDisplay(nextTier.minSpend)}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${Math.min(100, (spend / nextTier.minSpend) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tier === 'platinum' && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-center text-purple-700 text-sm font-medium">
          🎉 You've reached the highest tier! Enjoy 15% off + free days every 5 rentals.
        </div>
      )}
    </div>
  );
}

// ── Main Profile Component ────────────────────────────────────────────────────

const TABS = ['Account', 'My Trips', 'Rewards'];

const Profile = () => {
  const api = useApi();
  const { user, isLoggedIn, loading, login, logout, isLoggingOut, displayName } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = TABS.find(t => t.toLowerCase().replace(' ', '-') === requestedTab) || 'Account';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [email, setEmail] = useState('');

  // Redirect if not logged in
  useEffect(() => {
    const hasCode = new URLSearchParams(window.location.search).has('code');
    if (loading || isLoggingOut) return;
    if (!isLoggedIn && !hasCode) { login(); return; }
    if (isLoggedIn) {
      setEmail(user?.profile?.email || '');
    }
  }, [user, isLoggedIn, loading, isLoggingOut, login]);

  const hasCode = new URLSearchParams(window.location.search).has('code');
  if (loading || hasCode) {
    return <div className="p-10 text-center text-gray-600">Syncing secure profile…</div>;
  }
  if (!isLoggedIn) return null;

  return (
    <div className="max-w-2xl mx-auto mt-8 mb-16 px-4">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-6 gap-1">
        {TABS.map((tab) => (
          <TabButton key={tab} label={tab} active={activeTab === tab} onClick={() => setActiveTab(tab)} />
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl shadow-md p-6">
        {activeTab === 'Account' && (
          <AccountTab
            api={api}
            user={user}
            displayName={displayName}
            email={email}
            onLogout={logout}
          />
        )}
        {activeTab === 'My Trips' && <BookingsTab api={api} />}
        {activeTab === 'Rewards'  && <RewardsTab  api={api} />}
      </div>
    </div>
  );
};

export default Profile;
